import React from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";
import Header from "../Header";
import Footer from "../Footer";
import WhatsAppFloat from "../WhatsAppFloat";
import PageMeta from "../PageMeta";
import { featurePages } from "../../data/pageConfig";

export default function Features() {
  const { lang } = useParams();
  const { t } = useTranslation("features");
  const prefix = lang ? `/${lang}` : "";

  return (
    <div className="min-h-screen bg-white">
      <PageMeta
        title={`${t("index.title")} — TrackTeam`}
        description={t("index.subtitle")}
      />
      <Header />

      {/* Hero */}
      <section className="relative pt-24 md:pt-32 pb-16 md:pb-24 bg-gradient-to-br from-slate-50 via-blue-50/40 to-purple-50/30 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-400/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-400/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1s" }}></div>
        </div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-gray-900 mb-6 leading-tight">
            {t("index.title")}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {t("index.subtitle")}
          </p>
        </div>
      </section>

      {/* Feature Grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featurePages.map((feature) => {
              const Icon = feature.icon;
              return (
                <Link
                  key={feature.slug}
                  to={`${prefix}/features/${feature.slug}`}
                  className="group bg-white rounded-2xl border border-gray-100 p-8 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                >
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center ${feature.shadowColor} shadow-lg mb-6`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#62329f] transition-colors">
                    {t(`${feature.key}.title`)}
                  </h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    {t(`${feature.key}.shortDesc`)}
                  </p>
                  <span className="inline-flex items-center gap-2 text-[#62329f] font-medium text-sm group-hover:gap-3 transition-all">
                    {t("learnMore")} <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-[#62329f] via-purple-700 to-indigo-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{t("cta.title")}</h2>
          <p className="text-lg text-purple-100 mb-8 max-w-2xl mx-auto">{t("cta.subtitle")}</p>
          <div className="flex justify-center gap-4">
            <a href="https://apps.apple.com/ae/app/trackteam/id6755401884" target="_blank" rel="noreferrer">
              <img src="/Image/logo_appstore.svg" alt="App Store" className="h-12 w-[162px] object-contain" loading="lazy" />
            </a>
            <a href="https://play.google.com/store/apps/details?id=com.washey.familytrack.trackteam&pcampaignid=web_share" target="_blank" rel="noreferrer">
              <img src="/Image/logo_playstore.svg" alt="Google Play" className="h-12 w-[162px] object-contain" loading="lazy" />
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
